import { createSelector } from 'reselect';

import { CheckoutState } from '../reducers';
import { PassengerLabels } from '../../types';
import { SelectedBaggage, SelectedExtraSeat } from '../../../BaggageService/types';
// import { getBaggageServices, getIsInsuranceSelected, getIsTransferSelected, getServices } from '../order/selectors';
import { getIsInsuranceSelected, getIsTransferSelected } from '../order/selectors';
import { PassengerState } from './reducers';
import { ServiceType } from '../../components/Checkout/OrderPaidServiceCards/types';
import { getPassengerStateCompatible } from '../selectedServices/selectors';
import { concatPassengerNames, getPassengerLabel, getPassengerNames } from '../../../utils';
import {
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	OrderAdditionalServiceGdsServiceServiceType,
	TravellerFieldEnum as OldTravellerFieldEnum,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment as BaggageService,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment
} from '@websky/graphql';
import { getEXSTServices } from '@shared/lib/seat-map';

// A workaround to fix cyclic dependencies. Fix this in the future.
const getOrder = (state: CheckoutState) => state.order;

const getServices = createSelector(getOrder, order => order.additionalServices.gdsServices?.services ?? []);

const getBaggageServices = createSelector(
	getServices,
	(services): GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[] =>
		services.filter(
			service => service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
		) as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[]
);
// Workaround end

export const getPassengerState = (state: CheckoutState) => state.passengers;

export const getPassengerLabels = createSelector(
	getPassengerState,
	(passengers): PassengerLabels => {
		const result: PassengerLabels = {};
		const passengersMap: { [passengerType: string]: number } = {};

		passengers.forEach(passenger => {
			const passengerType = passenger.supplierTravellerType ?? passenger.type;

			if (!passengersMap.hasOwnProperty(passengerType)) {
				passengersMap[passengerType] = 0;
			}

			const passengerIndex = ++passengersMap[passengerType];

			const { firstName, lastName } = getPassengerNames(passenger);

			if (firstName && lastName) {
				result[passenger.id] = concatPassengerNames({ lastName, firstName });
			} else {
				result[passenger.id] = `${getPassengerLabel(passenger)} ${passengerIndex}`;
			}
		});

		return result;
	}
);

export const getPassengerNationalities = createSelector(
	getPassengerState,
	(passengers): Map<String, String> => {
		const result: Map<String, String> = new Map();

		const nationalityField = passengers[0].values.find(field => field.type === OldTravellerFieldEnum.Nationality);

		nationalityField &&
			nationalityField.validationRules &&
			nationalityField.validationRules[0].options.forEach(option => {
				if (!result.hasOwnProperty(option.value)) {
					result[option.value] = option.label;
				}
			});

		return result;
	}
);

export const makeHasAnySelectedBaggage = (passengerId: number) =>
	createSelector(getPassengerStateCompatible, getBaggageServices, (passengers, baggageServices) => {
		return passengers[passengerId].services.some(selectedService =>
			baggageServices.some(baggage => baggage.id === selectedService.serviceId)
		);
	});

export const createSelectedBaggage = (
	passengers: PassengerState[],
	baggageServices: BaggageService[]
): SelectedBaggage[] => {
	const result: SelectedBaggage[] = [];

	passengers.forEach(({ id, services }) => {
		const selectedBaggage: SelectedBaggage = {};

		services.forEach(selectedService => {
			if (baggageServices.some(baggage => baggage.id === selectedService.serviceId)) {
				selectedService.segmentIds.forEach(segmentId => {
					if (!selectedBaggage.hasOwnProperty(selectedService.serviceId)) {
						selectedBaggage[selectedService.serviceId] = {};
					}

					if (selectedBaggage[selectedService.serviceId].hasOwnProperty(segmentId)) {
						selectedBaggage[selectedService.serviceId][segmentId].count += selectedService.count;
					} else {
						selectedBaggage[selectedService.serviceId][segmentId] = {
							...selectedService
						};
					}
				});
			}
		});

		result[id] = selectedBaggage;
	});

	return result;
};

export const makeHasSelectedService = (type: ServiceType) => {
	return createSelector(
		getServices,
		getIsInsuranceSelected,
		getIsTransferSelected,
		getPassengerStateCompatible,
		(orderServices, isInsuranceSelected, isTransferSelected, passengers) => {
			return passengers.some(passenger =>
				passenger.services.some(travellerService => {
					switch (type) {
						case ServiceType.Baggage:
							return orderServices.find(
								orderService =>
									orderService.id === travellerService.serviceId &&
									orderService.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
							);

						case ServiceType.Meal:
							return orderServices.find(
								orderService =>
									orderService.id === travellerService.serviceId &&
									orderService.type === OrderAdditionalServiceGdsServiceServiceType.Meal
							);

						case ServiceType.Seats:
							return orderServices.find(
								orderService =>
									orderService.id === travellerService.serviceId &&
									orderService.type === OrderAdditionalServiceGdsServiceServiceType.Seat
							);

						case ServiceType.Insurance:
							return isInsuranceSelected;

						case ServiceType.Transfer:
							return isTransferSelected;
					}
				})
			);
		}
	);
};

export const getSelectedBaggage = createSelector(
	getPassengerStateCompatible,
	getBaggageServices,
	createSelectedBaggage
);

export const createSelectedExtraSeats = (
	passengers: PassengerState[],
	extraSeatServices: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
) => {
	let selectedExtraSeats: SelectedExtraSeat[] = [];
	const extraSeatIds = extraSeatServices.map(service => service.id);

	passengers.forEach(passenger => {
		passenger.services?.forEach(service => {
			if (extraSeatIds.includes(service.serviceId)) {
				selectedExtraSeats = [
					...selectedExtraSeats,
					{
						...service,
						passengerId: passenger.id
					}
				];
			}
		});
	});

	return selectedExtraSeats;
};

export const getExtraSeatServices = createSelector(getServices, getEXSTServices);

export const getSelectedExtraSeats = createSelector(
	getPassengerStateCompatible,
	getExtraSeatServices,
	createSelectedExtraSeats
);
